import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

type ToastKind = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  show(kind: ToastKind, message: string): void;
  success(message: string): void;
  error(message: string): void;
  info(message: string): void;
}

const ToastCtx = createContext<ToastApi | null>(null);

let nextId = 1;

export function ToasterProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId++;
      setToasts((cur) => [...cur, { id, kind, message }]);
      const ttl = kind === 'error' ? 8000 : 4000;
      setTimeout(() => remove(id), ttl);
    },
    [remove],
  );

  const api: ToastApi = {
    show,
    success: (m) => show('success', m),
    error: (m) => show('error', m),
    info: (m) => show('info', m),
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex flex-col items-center gap-2 px-4">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={
              'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-[var(--radius-card)] border px-4 py-3 shadow-lg ' +
              (t.kind === 'success'
                ? 'border-green-300 bg-green-50 text-green-900'
                : t.kind === 'error'
                  ? 'border-red-300 bg-red-50 text-red-900'
                  : 'border-blue-300 bg-blue-50 text-blue-900')
            }
            role="status"
          >
            {t.kind === 'success' ? (
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-700" />
            ) : t.kind === 'error' ? (
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-700" />
            ) : (
              <Info className="h-5 w-5 flex-shrink-0 text-blue-700" />
            )}
            <div className="flex-1 whitespace-pre-wrap text-sm">{t.message}</div>
            <button
              type="button"
              onClick={() => remove(t.id)}
              className="text-current opacity-60 hover:opacity-100"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    throw new Error('useToast must be used inside ToasterProvider');
  }
  return ctx;
}
